import { apiClient } from './client';

export interface TableImage {
  tableMarkdown: string;
  imageUrl: string;
  tableIndex: number;
}

export interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  searchResults?: SearchResult[];
  tableImages?: TableImage[];
}

export interface SearchResult {
  title: string;
  source: string;
  score?: number;
}

export interface ChatResponse {
  message: string;
  sessionId: string;
  searchResults: SearchResult[];
  modelName: string;
  tableImages?: TableImage[];
  user: {
    id: string;
    email: string;
    name: string;
  };
}

export interface ChatSession {
  sessionId: string;
  lastMessage: string;
  lastMessageTime: string;
  messageCount: number;
}

export interface SessionMessagesResponse {
  sessionId: string;
  messages: Array<{
    id?: string; // Database UNIQUEIDENTIFIER
    role: 'user' | 'assistant';
    message: string;
    timestamp: string;
  }>;
}

export interface OperationStatus {
  success: boolean;
  operation: {
    id: string;
    operation_type: string;
    status: 'started' | 'in_progress' | 'completed' | 'error';
    progress: {
      percent: number;
      step: string | null;
      history: Array<any>;
      progress_text: string | null;
      result: any;
    };
    progress_text: string | null;
    result: any;
    error_message: string | null;
  };
}

export const chatApi = {
  sendMessage: async (message: string, sessionId?: string): Promise<{ operationId: string; status: string }> => {
    const body: any = { message };
    if (sessionId) {
      body.sessionId = sessionId;
    }
    return apiClient.post<{ operationId: string; status: string }>('/api/mobile/chat', body);
  },

  getOperationStatus: async (operationId: string): Promise<OperationStatus> => {
    return apiClient.get<OperationStatus>(`/api/operations/${operationId}`);
  },

  getSessions: async (limit: number = 10): Promise<ChatSession[]> => {
    const response = await apiClient.get<{ sessions: ChatSession[] }>(
      `/api/mobile/chat/sessions?limit=${limit}`
    );
    return response.sessions;
  },

  createNewSession: async (): Promise<string> => {
    const response = await apiClient.post<{ sessionId: string }>('/api/mobile/chat/sessions/new', {});
    return response.sessionId;
  },

  getSessionMessages: async (sessionId: string): Promise<SessionMessagesResponse> => {
    return apiClient.get<SessionMessagesResponse>(`/api/mobile/chat/sessions/${sessionId}/messages`);
  },

  deleteSession: async (sessionId: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/api/mobile/chat/sessions/${sessionId}`);
  },

  submitFeedback: async (messageId: string, rating: 'positive' | 'negative', feedbackText?: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.post<{ success: boolean; message: string }>('/api/mobile/chat/feedback', {
      messageId,
      rating,
      feedback: feedbackText || ''
    });
  },

  deleteFeedback: async (messageId: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/api/mobile/chat/feedback/${messageId}`);
  },
};
